import { Button, Typography } from '@material-ui/core'
import Skeleton from '@material-ui/lab/Skeleton'
import { makeStyles } from '@material-ui/core/styles'
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon'
import CheckCircleOutlineIcon from 'mdi-react/CheckCircleOutlineIcon'
import MapMarkerOutlineIcon from 'mdi-react/MapMarkerOutlineIcon'
import OpenInNewIcon from 'mdi-react/OpenInNewIcon'
import { FC, ReactNode, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, Redirect, useLocation, useParams } from 'react-router-dom'
import { selectUserIsAuthenticated } from '../../selectors/users'
import { CatalogApartment } from '../../services/catalog'
import {
  availability,
  bedroomsLabel,
  depositLabel,
  energyLabel,
  featureList,
  floorLabel,
  formatArea,
  formatDate,
  formatEuro,
  layoutTitle,
  listingTitle,
  locationLine,
  optionLabel,
  waterLabel
} from './format'
import Gallery from './Gallery'
import { useCatalog, useCatalogPage } from './useCatalog'

const useStyles = makeStyles((theme) => ({
  page: {
    maxWidth: 1240,
    margin: '0 auto',
    padding: '24px 24px 96px',
    color: '#1f2421',
    [theme.breakpoints.down('xs')]: {
      padding: '12px 0 120px'
    }
  },
  gutter: {
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 16,
      paddingRight: 16
    }
  },
  back: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 40,
    padding: '0 16px 0 12px',
    marginTop: 16,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 8,
    color: theme.palette.primary.main,
    fontFamily: 'CircularBold',
    fontSize: 15,
    textDecoration: 'none',
    transition: 'background 150ms ease',
    '&:hover': { background: '#eef6ec' },
    [theme.breakpoints.down('xs')]: {
      marginTop: 8
    }
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 360px',
    gap: 40,
    marginTop: 28,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 24,
      marginTop: 20
    }
  },
  title: {
    fontSize: 32,
    lineHeight: 1.2,
    margin: 0,
    [theme.breakpoints.down('xs')]: {
      fontSize: 26
    }
  },
  address: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px 6px',
    marginTop: 10,
    fontSize: 17,
    color: '#5b635e',
    '& svg': { flex: '0 0 auto', color: '#8a938d' }
  },
  facts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 1,
    marginTop: 24,
    background: '#e5e7e6',
    border: '1px solid #e5e7e6',
    borderRadius: 12,
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      '& > div:last-child:nth-child(odd)': { gridColumn: '1 / -1' }
    }
  },
  fact: {
    background: '#fff',
    padding: '14px 16px'
  },
  factLabel: {
    fontSize: 13,
    color: '#8a938d'
  },
  factValue: {
    fontFamily: 'CircularBold',
    fontSize: 18,
    marginTop: 2
  },
  section: {
    marginTop: 40
  },
  sectionTitle: {
    fontSize: 22,
    margin: '0 0 16px'
  },
  description: {
    whiteSpace: 'pre-line',
    fontSize: 16,
    lineHeight: 1.65,
    color: '#2d332f'
  },
  translationNote: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    fontSize: 14,
    color: '#8a938d'
  },
  linkButton: {
    border: 0,
    padding: 0,
    background: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    font: 'inherit',
    textDecoration: 'underline'
  },
  features: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10
  },
  feature: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 999,
    background: '#eef6ec',
    color: '#2f6b27',
    fontSize: 15
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 15,
    '& th, & td': {
      padding: '12px 0',
      borderBottom: '1px solid #eceeed',
      textAlign: 'left',
      verticalAlign: 'top'
    },
    '& th': {
      width: '42%',
      fontWeight: 'normal',
      color: '#8a938d',
      paddingRight: 16
    }
  },
  mapLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontFamily: 'CircularBold',
    fontSize: 15,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { textDecoration: 'underline' },
    '& svg': { color: 'inherit' },
    [theme.breakpoints.up('sm')]: { marginLeft: 10 }
  },
  sidebar: {
    position: 'sticky',
    top: 93,
    alignSelf: 'start',
    padding: 24,
    borderRadius: 12,
    border: '1px solid #e5e7e6',
    boxShadow: '0 10px 30px rgba(31,36,33,0.08)',
    background: '#fff',
    [theme.breakpoints.down('sm')]: {
      position: 'static',
      boxShadow: 'none'
    },
    [theme.breakpoints.down('xs')]: {
      margin: '0 16px'
    }
  },
  rent: {
    fontFamily: 'CircularBold',
    fontSize: 32,
    lineHeight: 1.1
  },
  rentUnit: {
    fontFamily: 'CircularRegular',
    fontSize: 16,
    color: '#5b635e'
  },
  sideRows: {
    margin: '18px 0 20px',
    fontSize: 15,
    '& > div': {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 16,
      padding: '8px 0',
      borderBottom: '1px solid #eceeed'
    },
    '& span:first-child': { color: '#8a938d' },
    '& span:last-child': { textAlign: 'right' }
  },
  applyButton: {
    borderRadius: 8,
    padding: '12px 20px',
    fontFamily: 'CircularBold',
    fontSize: 17,
    textTransform: 'none',
    boxShadow: 'none'
  },
  mobileBar: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 10,
      padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
      background: '#fff',
      borderTop: '1px solid #e5e7e6',
      boxShadow: '0 -4px 16px rgba(0,0,0,0.06)'
    }
  },
  state: {
    textAlign: 'center',
    padding: '80px 16px',
    color: '#5b635e',
    '& svg': { color: '#9aa39c' }
  }
}))

type Row = [string, ReactNode | undefined]

const DetailTable: FC<{ rows: Row[] }> = ({ rows }) => {
  const classes = useStyles()
  const visible = rows.filter(([, value]) => value !== undefined && value !== null && value !== '')
  if (!visible.length) return null
  return (
    <table className={classes.table}>
      <tbody>
        {visible.map(([label, value]) => (
          <tr key={label}>
            <th scope='row'>{label}</th>
            <td>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const Description: FC<{ apartment: CatalogApartment }> = ({ apartment }) => {
  const classes = useStyles()
  const [showOriginal, setShowOriginal] = useState(false)
  const original = (apartment.description || '').trim()
  const translated = apartment.descriptionEn?.trim()
  if (!original && !translated) return null
  const text = translated && !showOriginal ? translated : original
  return (
    <section className={classes.section}>
      <Typography variant='h2' className={classes.sectionTitle}>
        About this home
      </Typography>
      <div className={classes.description} lang={translated && !showOriginal ? 'en' : 'fi'}>
        {text}
      </div>
      <div className={classes.translationNote}>
        {translated ? (
          <>
            <span>{showOriginal ? 'Original description in Finnish.' : 'Automatically translated from Finnish.'}</span>
            <button type='button' className={classes.linkButton} onClick={() => setShowOriginal(!showOriginal)}>
              {showOriginal ? 'Show English translation' : 'Show original'}
            </button>
          </>
        ) : (
          <span>Description in Finnish. An English translation will be available shortly.</span>
        )}
      </div>
    </section>
  )
}

const ApplyButton: FC<{ apartment: CatalogApartment; fullWidth?: boolean }> = ({ apartment, fullWidth }) => {
  const classes = useStyles()
  return (
    <Button
      href={`/new-application/${apartment.code}?lang=en`}
      variant='contained'
      color='primary'
      fullWidth={fullWidth}
      className={classes.applyButton}
    >
      Apply for this home
    </Button>
  )
}

const pageTitle = (apartment: CatalogApartment) =>
  `${listingTitle(apartment)} – ${[apartment.address, locationLine(apartment)].filter(Boolean).join(', ')} | Fiksuvuokraus`

const Listing: FC<{ apartment: CatalogApartment }> = ({ apartment }) => {
  const classes = useStyles()
  const title = listingTitle(apartment)
  const isAuthenticated = useSelector(selectUserIsAuthenticated)
  const catalogSearch = (useLocation().state as { catalogSearch?: string } | undefined)?.catalogSearch || ''
  const place = [apartment.address, locationLine(apartment)].filter(Boolean).join(', ')
  const mapsQuery = [apartment.address, apartment.postalCode, apartment.municipality].filter(Boolean).join(', ')
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`

  const rent = formatEuro(apartment.price)
  const features = featureList(apartment)
  const facts: Row[] = [
    ['Rent', rent ? `${rent} / mo` : undefined],
    ['Size', formatArea(apartment.squareMeters)],
    ['Layout', bedroomsLabel(apartment.rooms)],
    ['Floor', floorLabel(apartment)],
    ['Available', availability(apartment)]
  ]

  return (
    <div className={classes.page}>
      <div className={classes.gutter}>
        <Link to={`/homes${catalogSearch}`} className={classes.back}>
          <ArrowLeftIcon size={18} /> All homes
        </Link>
      </div>

      <div className={classes.layout}>
        <div className={classes.gutter}>
          <Gallery images={apartment.images} alt={title} />
          <Typography variant='h1' className={classes.title}>
            {title}
          </Typography>
          <div className={classes.address}>
            <MapMarkerOutlineIcon size={19} />
            <span>{place}</span>
            <a className={classes.mapLink} href={mapsUrl} target='_blank' rel='noopener noreferrer'>
              Open in Google Maps <OpenInNewIcon size={15} />
            </a>
          </div>

          <div className={classes.facts}>
            {facts
              .filter(([, value]) => value)
              .map(([label, value]) => (
                <div key={label} className={classes.fact}>
                  <div className={classes.factLabel}>{label}</div>
                  <div className={classes.factValue}>{value}</div>
                </div>
              ))}
          </div>

          <Description apartment={apartment} />

          {features.length > 0 && (
            <section className={classes.section}>
              <Typography variant='h2' className={classes.sectionTitle}>
                Highlights
              </Typography>
              <div className={classes.features}>
                {features.map((feature) => (
                  <span key={feature} className={classes.feature}>
                    <CheckCircleOutlineIcon size={17} /> {feature}
                  </span>
                ))}
              </div>
            </section>
          )}

          <section className={classes.section}>
            <Typography variant='h2' className={classes.sectionTitle}>
              Apartment details
            </Typography>
            <DetailTable
              rows={[
                ['Layout', layoutTitle(apartment)],
                ['Living area', formatArea(apartment.squareMeters)],
                ['Floor', floorLabel(apartment)],
                ['Building type', optionLabel('buildingType', apartment.buildingType)],
                ['Year built', apartment.buildYear],
                ['Lift', apartment.hasElevator ? 'Yes' : undefined],
                ['Furnished', apartment.isFurnished ? 'Yes' : 'No'],
                ['Condition', optionLabel('condition', apartment.condition)],
                ['Balcony', optionLabel('balcony', apartment.balcony)],
                ['Sauna', optionLabel('sauna', apartment.sauna)],
                ['Storage', optionLabel('storage', apartment.storage)],
                ['Dishwasher', optionLabel('dishwasher', apartment.dishwasher)],
                ['Washing machine', optionLabel('laundryMachine', apartment.laundryMachine)],
                ['Heating', optionLabel('heating', apartment.heating)],
                ['Parking', optionLabel('parking', apartment.parking)],
                ['Pets', optionLabel('petsAllowed', apartment.petsAllowed)],
                ['Energy class', energyLabel(apartment.energyCertification)]
              ]}
            />
          </section>

          <section className={classes.section}>
            <Typography variant='h2' className={classes.sectionTitle}>
              Rent and costs
            </Typography>
            <DetailTable
              rows={[
                ['Rent', rent ? `${rent} / month` : undefined],
                ['Water', waterLabel(apartment)],
                ['Parking fee', apartment.parkingFee ? `${formatEuro(apartment.parkingFee)} / month` : undefined],
                ['Security deposit', depositLabel(apartment)],
                ['Lease type', optionLabel('rentPeriod', apartment.rentPeriod)],
                ['Availability', availability(apartment)],
                ['Available until', formatDate(apartment.availableUntilDate)]
              ]}
            />
          </section>

        </div>

        <aside className={`${classes.sidebar} ${classes.gutter}`}>
          <div className={classes.rent}>
            {rent || 'Rent on request'} {rent && <span className={classes.rentUnit}>/ month</span>}
          </div>
          <div className={classes.sideRows}>
            <div>
              <span>Available</span>
              <span>{availability(apartment)}</span>
            </div>
            {waterLabel(apartment) && (
              <div>
                <span>Water</span>
                <span>{waterLabel(apartment)}</span>
              </div>
            )}
            {depositLabel(apartment) && (
              <div>
                <span>Deposit</span>
                <span>{depositLabel(apartment)}</span>
              </div>
            )}
          </div>
          <ApplyButton apartment={apartment} fullWidth />
        </aside>
      </div>

      {/* logged-in users have the app's bottom navigation in this spot */}
      {!isAuthenticated && (
        <div className={classes.mobileBar}>
          <div className={classes.rent} style={{ fontSize: 22 }}>
            {rent || 'Rent on request'} {rent && <span className={classes.rentUnit}>/ mo</span>}
          </div>
          <ApplyButton apartment={apartment} />
        </div>
      )}
    </div>
  )
}

const CatalogListing: FC = () => {
  const classes = useStyles()
  const { code } = useParams<{ code: string }>()
  const { apartments, error, retry } = useCatalog()
  const found = apartments?.find((item) => item.code === code)
  useCatalogPage(found ? pageTitle(found) : undefined)

  if (error) {
    return (
      <div className={classes.state}>
        <Typography variant='h5' gutterBottom>
          We couldn't load this listing
        </Typography>
        <Button variant='contained' color='primary' onClick={retry} style={{ marginTop: 12 }}>
          Try again
        </Button>
      </div>
    )
  }

  if (!apartments) {
    return (
      <div className={classes.page}>
        <Skeleton variant='rect' height={420} style={{ borderRadius: 12 }} />
        <div className={classes.gutter}>
          <Skeleton width='55%' height={48} style={{ marginTop: 24 }} />
          <Skeleton width='40%' height={28} />
          <Skeleton variant='rect' height={80} style={{ marginTop: 24, borderRadius: 12 }} />
        </div>
      </div>
    )
  }

  // expired link (rented or withdrawn): the catalog is the useful landing
  if (!found) return <Redirect to='/homes' />

  // key: a new listing starts from its first photo and the translated description
  return <Listing key={found.code} apartment={found} />
}

export default CatalogListing
